import { Component, forwardRef, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ControlValueAccessor, NG_VALUE_ACCESSOR, FormControl, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { AddCheckboxConsentComponent } from './add-checkbox-consent.component';
import { Consent, ConsentType, CheckboxConsent, ConsentCategory } from '@models/consent.model';

@Component({
  selector: 'app-add-nhh-consent',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatCheckboxModule,
    AddCheckboxConsentComponent
  ],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => AddNhhConsentComponent),
      multi: true
    }
  ],
  template: `
    <div class="mt-3">
      <h5>Consent</h5>
      <p>
        For non-half-hourly data, we need your consent to access your meter data.
      </p>
      <div class="consent-checkbox">
        <app-add-checkbox-consent [formControl]="checkboxConsentControl">
          By proceeding you agree to our 
          <a href="https://www.perse.io/terms" target="_blank">Terms & Conditions</a> 
          and 
          <a href="https://www.perse.io/privacy" target="_blank">Privacy Policy</a> 
          which include retrieving your current and historical energy information.
        </app-add-checkbox-consent>
      </div>
    </div>
  `,
  styles: [`
    .consent-checkbox {
      margin-top: 1rem; 
    }
  `]
})
export class AddNhhConsentComponent implements ControlValueAccessor {
  
  // Internal checkbox consent control
  checkboxConsentControl = new FormControl<CheckboxConsent | null>(null);
  
  // Internal value - standardized to always be a Consent object
  private _value: Consent | null = null;
  
  // Control value accessor methods - standardized to work with Consent type
  private _onChange = (value: Consent | null) => {};
  private _onTouched = () => {};

  constructor() {
    // Subscribe to checkbox consent changes
    this.checkboxConsentControl.valueChanges.subscribe(checkboxConsent => {
      if (checkboxConsent) {
        const consent = this.createConsentFromCheckbox(checkboxConsent);
        this._value = consent;
        this._onChange(consent);
        this._onTouched();
      } else {
        this._value = null;
        this._onChange(null);
      }
    });
  }

  // Helper method to create standardized Consent object from CheckboxConsent
  // This ensures the form control always returns a value of Consent type
  private createConsentFromCheckbox(checkboxConsent: CheckboxConsent): Consent {
    return {
      consentCategory: ConsentCategory.CHECKBOX,
      consentType: ConsentType.CHECKBOX,
      consentData: checkboxConsent,
      scope: 'ANNUAL'
    };
  }

  writeValue(value: Consent | null): void {
    this._value = value;
    
    // Update the checkbox consent control based on the consent value
    if (value && value.consentType === ConsentType.CHECKBOX) {
      // Extract the CheckboxConsent from the consent data
      if (typeof value.consentData === 'object' && 'accepted' in value.consentData) {
        this.checkboxConsentControl.setValue(value.consentData as CheckboxConsent, { emitEvent: false });
      }
    } else {
      this.checkboxConsentControl.setValue(null, { emitEvent: false });
    }
  }

  registerOnChange(fn: (value: Consent | null) => void): void {
    this._onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this._onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    if (isDisabled) {
      this.checkboxConsentControl.disable();
    } else {
      this.checkboxConsentControl.enable();
    }
  }
}
